import { cn } from '@/lib/utils';
import {
  coverageCardClass,
  coverageElementGridClass,
  coverageMetricsGridClass,
  coveragePageClass,
} from './coverage-dashboard-layout';

export type CoverageSummaryVariant = 'combined' | 'automation' | 'manual';

const METRIC_CARD_COUNTS: Record<CoverageSummaryVariant, number> = {
  combined: 6,
  automation: 5,
  manual: 5,
};

const SkeletonBar = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'animate-pulse rounded-md bg-[var(--inv-surface-muted)]',
      className,
    )}
  />
);

const CoverageMetricCardSkeleton = ({ withProgress = true }: { withProgress?: boolean }) => (
  <div className="min-w-0 rounded-lg border border-solid border-[var(--inv-border)] border-l-[3px] border-l-[var(--inv-border-soft)] bg-[var(--inv-surface)] px-3 py-2">
    <SkeletonBar className="mb-1 h-2.5 w-16" />
    <SkeletonBar className="h-5 w-14" />
    {withProgress ? <SkeletonBar className="mt-1.5 h-1 w-full rounded-full" /> : null}
    <SkeletonBar className="mt-1 h-3 w-24" />
  </div>
);

export const CoverageCodeModalSkeleton = () => (
  <div className="flex flex-col gap-2.5 py-2" aria-hidden="true">
    {Array.from({ length: 14 }).map((_, index) => (
      <SkeletonBar
        key={index}
        className={cn(
          'h-3',
          index % 4 === 0 ? 'w-[92%]' : index % 3 === 0 ? 'w-[78%]' : 'w-[62%]',
        )}
      />
    ))}
  </div>
);

export function CoverageDashboardToolbarSkeleton() {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <SkeletonBar className="mb-1.5 h-4 w-44" />
        <SkeletonBar className="h-3.5 w-56" />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <SkeletonBar className="h-9 w-24 rounded-lg" />
        <SkeletonBar className="h-9 w-[13.5rem] rounded-[10px]" />
      </div>
    </div>
  );
}

export function CoverageDashboardContentSkeleton({
  variant = 'combined',
}: {
  variant?: CoverageSummaryVariant;
}) {
  const metricCount = METRIC_CARD_COUNTS[variant];

  return (
    <div aria-busy="true" aria-label="Loading coverage data">
      <SkeletonBar className="mb-4 h-14 w-full rounded-[10px]" />

      <div className={coverageMetricsGridClass}>
        {Array.from({ length: metricCount }).map((_, index) => (
          <CoverageMetricCardSkeleton
            key={index}
            withProgress={index < 4}
          />
        ))}
      </div>

      <div className={cn(coverageElementGridClass, 'mt-0')}>
        {Array.from({ length: 5 }).map((_, index) => (
          <CoverageMetricCardSkeleton key={index} />
        ))}
      </div>

      <div className="mb-5 mt-1">
        <SkeletonBar className="mb-3 h-5 w-40" />
        <div className="overflow-hidden rounded-[10px] border border-solid border-[var(--inv-border)]">
          <div className="flex gap-4 border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-4 py-2.5">
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonBar
                key={index}
                className={cn(
                  'h-3',
                  index === 0 ? 'w-24' : index === 4 ? 'ml-auto w-16' : 'w-16',
                )}
              />
            ))}
          </div>
          {Array.from({ length: 7 }).map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="flex items-center gap-4 border-0 border-b border-solid border-[var(--inv-border-soft)] px-4 py-3 last:border-b-0">
              <SkeletonBar
                className={cn(
                  'h-3.5',
                  rowIndex % 3 === 0 ? 'w-[45%]' : rowIndex % 2 === 0 ? 'w-[38%]' : 'w-[32%]',
                )}
              />
              <SkeletonBar className="h-3.5 w-12" />
              <SkeletonBar className="hidden h-3.5 w-14 min-[640px]:block" />
              <SkeletonBar className="hidden h-3.5 w-12 min-[640px]:block" />
              <SkeletonBar className="hidden h-3.5 w-12 min-[768px]:block" />
              <SkeletonBar className="ml-auto h-7 w-14 shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CoverageDashboardCardSkeleton({
  variant = 'combined',
}: {
  variant?: CoverageSummaryVariant;
}) {
  return (
    <section className={cn(coverageCardClass, 'animate-pulse')} aria-hidden="true">
      <CoverageDashboardToolbarSkeleton />
      <CoverageDashboardContentSkeleton variant={variant} />
    </section>
  );
}

const CoverageDashboardSkeleton = ({
  variant = 'combined',
}: {
  variant?: CoverageSummaryVariant;
}) => (
  <div
    className={cn(coveragePageClass, 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading coverage dashboard">
    <header className="mb-5 max-w-[42rem]">
      <SkeletonBar className="mb-3 h-2.5 w-28" />
      <SkeletonBar className="mb-2 h-8 w-[min(100%,14rem)]" />
      <SkeletonBar className="h-4 w-[min(100%,34rem)]" />
    </header>

    <SkeletonBar className="mb-4 h-11 w-full rounded-xl" />

    <CoverageDashboardCardSkeleton variant={variant} />
  </div>
);

export default CoverageDashboardSkeleton;
